import {
  Button,
  Stack,
  List,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import type { JSX } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Transfer } from '@/api/definition'
import { Status } from '@/api/definition'
import { fetchEndpoints, takeIn } from '@/api/external_actions'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import DeviceListItem from '@/pages/intake/component/DeviceListItem'
import { SelectedDevicesContext } from '@/pages/intake/reducers'
import types from '@/pages/Device/DeviceType'
import type { Endpoints } from '@/pages/utils/endpointSchema'
import EndpointsSchema from '@/pages/utils/endpointSchema'
import useResponseValidator from '@/pages/utils/responseValidator'

interface PutIntoServiceStepProps {
  setActiveStep: (step: number) => void
}

const PutIntoServiceStep = ({
  setActiveStep,
}: PutIntoServiceStepProps): JSX.Element => {
  const { t } = useTranslation(['di', 'app', 'common'])

  const [confirmed, setConfirmed] = useState(false)
  const [endpoints, setEndpoints] = useState<Endpoints | null>(null)
  const [endpoint] = useState('')

  const [requests, setRequests] = useState<Map<string, Transfer>>(new Map())

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { validate, showTransferError } = useResponseValidator()
  const [statusBeingUpdated, setStatusBeingUpdated] = useState(false)

  const { state: selectedDevicesState } = useContext(SelectedDevicesContext)

  const selectedDevices = useMemo(
    () => Object.values(selectedDevicesState.selectedDevices),
    [selectedDevicesState],
  )

  useEffect(() => {
    let isCancelled = false

    const loadEndpoints = async () => {
      if (endpoints) return

      try {
        const response = await fetchEndpoints()

        if (isCancelled) return

        const validatedEndpoints = await validate<Endpoints>(
          EndpointsSchema,
          response,
        )
        setEndpoints(validatedEndpoints)
      } catch (error) {
        console.error('Failed to load endpoints:', error)
      }
    }

    loadEndpoints()

    return () => {
      isCancelled = true
    }
  }, [endpoints, validate])

  useEffect(() => {
    setConfirmed(false)
    // Reset requests when selected devices change
    setRequests(new Map())
  }, [selectedDevices])

  const updateServiceStatus = useCallback(
    async (uid: string) => {
      const oldRequest = requests.get(uid)
      if (oldRequest && oldRequest.status !== Status.FAILURE) return

      setRequests((prevRequests) => {
        const newRequests = new Map(prevRequests)
        newRequests.set(uid, { status: Status.IN_PROGRESS })
        return newRequests
      })

      const result = await takeIn(uid, endpoint)

      // Show HTTP error details if available
      showTransferError(result)

      setRequests((prevRequests) => {
        const newRequests = new Map(prevRequests)
        newRequests.set(uid, result)
        return newRequests
      })
    },
    [endpoint, requests, showTransferError],
  )

  const updateServiceStatuses = useCallback(async () => {
    if (statusBeingUpdated) return

    setStatusBeingUpdated(true)
    console.log('Starting batch update for', selectedDevices.length, 'devices')

    try {
      for (const device of selectedDevices) {
        await updateServiceStatus(device.uid)
      }
    } finally {
      setStatusBeingUpdated(false)
    }
  }, [selectedDevices, statusBeingUpdated, updateServiceStatus])

  const handleBack = useCallback(() => {
    console.log(
      'Navigating back with selections:',
      Object.keys(selectedDevicesState.selectedDevices),
    )

    setActiveStep(0)
  }, [selectedDevicesState.selectedDevices, setActiveStep])

  const handleConfirm = async () => {
    setIsDialogOpen(false)
    setConfirmed(true)
    await updateServiceStatuses()
  }

  const handleActionClick = () => {
    if (confirmed) {
      updateServiceStatuses()
    } else {
      setIsDialogOpen(true)
    }
  }

  const hasSelectedDevices = selectedDevices.length > 0

  return (
    <Box
      sx={{ width: '100%', backgroundColor: '#f5f5f5', borderRadius: 1, p: 2 }}
    >
      {hasSelectedDevices ? (
        <List
          sx={{
            width: '100%',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          {selectedDevices.map(({ type, serial, label, uid }) => (
            <DeviceListItem
              key={uid}
              serialNr={serial}
              ownerLabel={label}
              deviceTypeName={types[type] ?? type}
              request={requests.get(uid)}
              onRetryClick={async () => updateServiceStatus(uid)}
            />
          ))}
        </List>
      ) : (
        <Typography variant="body1" sx={{ p: 2 }}>
          {t('app:device.noDevicesSelected')}
        </Typography>
      )}

      {statusBeingUpdated && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 3, justifyContent: 'flex-start', alignItems: 'center' }}
      >
        <Button
          variant="backbutton"
          onClick={handleBack}
          sx={{
            marginTop: 2,
            marginRight: 2,
            color: '#fff',
          }}
        >
          {t('common:general.back')}
        </Button>

        <Button
          variant="contained"
          color="success"
          disabled={!hasSelectedDevices || statusBeingUpdated}
          onClick={handleActionClick}
        >
          {t('app:device.intake.putIntoService')}
        </Button>
      </Stack>

      <ConfirmDialog
        onConfirm={handleConfirm}
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
      >
        {t('app:device.intake.willPutAllIntoService')}
      </ConfirmDialog>
    </Box>
  )
}

export default PutIntoServiceStep
